<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import InboxTeamsRoutingTable from './InboxTeamsRoutingTable.vue';
import PageHeader from '../SettingsSubPageHeader.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const selectedTeamIds = ref([]);
const isCreating = ref(false);
const teamConfigs = reactive({});
const defaultTeamId = ref(null);

const rules = {
  selectedTeamIds: {
    isEmpty: value => !!value.length,
  },
};
const v$ = useVuelidate(rules, { selectedTeamIds });

const teamList = computed(() => store.getters['teams/getTeams']);
const routingTypes = computed(
  () => store.getters['routingTypes/getRoutingTypes']
);
const agentBots = computed(() => store.getters['agentBots/getBots']);

const selectedTeamNames = computed(() =>
  selectedTeamIds.value.map(
    id => teamList.value.find(team => team.id === id)?.name ?? ''
  )
);

const teamMenuItems = computed(() =>
  teamList.value
    .filter(({ id }) => !selectedTeamIds.value.includes(id))
    .map(({ id, name }) => ({
      label: name,
      value: id,
      action: 'select',
    }))
);

const selectedTeams = computed(() =>
  selectedTeamIds.value
    .map(id => teamList.value.find(team => team.id === id))
    .filter(Boolean)
);

const ensureTeamConfig = teamId => {
  if (!teamConfigs[teamId]) {
    teamConfigs[teamId] = { routing_type_id: '', agent_bot_id: '' };
  }
};

const handleTeamAdd = ({ value }) => {
  if (!selectedTeamIds.value.includes(value)) {
    selectedTeamIds.value.push(value);
    ensureTeamConfig(value);
    if (!defaultTeamId.value) {
      defaultTeamId.value = value;
    }
  }
};

const handleTeamRemove = index => {
  const [removedId] = selectedTeamIds.value.splice(index, 1);
  delete teamConfigs[removedId];
  if (defaultTeamId.value === removedId) {
    defaultTeamId.value = selectedTeamIds.value[0] ?? null;
  }
};

const handleConfigUpdate = ({ teamId, field, value }) => {
  ensureTeamConfig(teamId);
  teamConfigs[teamId][field] = value;
};

const addTeams = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (!defaultTeamId.value) {
    useAlert(t('INBOX_MGMT.TEAMS.ROUTING.DEFAULT_TEAM_REQUIRED'));
    return;
  }

  isCreating.value = true;
  const inboxId = route.params.inbox_id;

  try {
    const teamConfigsPayload = selectedTeamIds.value.map(teamId => ({
      team_id: teamId,
      is_default: teamId === defaultTeamId.value,
      routing_type_id: teamConfigs[teamId]?.routing_type_id || null,
      agent_bot_id: teamConfigs[teamId]?.agent_bot_id || null,
    }));

    await store.dispatch('inboxTeams/update', {
      inboxId,
      teamConfigs: teamConfigsPayload,
    });

    router.replace({
      name: 'settings_inbox_finish',
      params: {
        page: 'new',
        inbox_id: inboxId,
      },
    });
  } catch (error) {
    useAlert(error.message);
  }
  isCreating.value = false;
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('teams/get'),
    store.dispatch('routingTypes/get'),
    store.dispatch('agentBots/get'),
  ]);
});
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="addTeams()">
      <div class="w-full">
        <PageHeader
          :header-title="$t('INBOX_MGMT.TEAMS.ADD_TITLE')"
          :header-content="$t('INBOX_MGMT.TEAMS.ADD_DESC')"
        />
      </div>
      <div>
        <div class="w-full mb-4">
          <label :class="{ error: v$.selectedTeamIds.$error }">
            {{ $t('INBOX_MGMT.TEAMS.TITLE') }}
            <div
              class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
            >
              <TagInput
                :model-value="selectedTeamNames"
                :placeholder="$t('INBOX_MGMT.TEAMS.PICK_TEAMS')"
                :menu-items="teamMenuItems"
                show-dropdown
                skip-label-dedup
                @add="handleTeamAdd"
                @remove="handleTeamRemove"
              />
            </div>
            <span v-if="v$.selectedTeamIds.$error" class="message">
              {{ $t('INBOX_MGMT.TEAMS.VALIDATION_ERROR') }}
            </span>
          </label>
        </div>

        <div v-if="selectedTeams.length" class="w-full mb-4">
          <InboxTeamsRoutingTable
            v-model:default-team-id="defaultTeamId"
            :selected-teams="selectedTeams"
            :team-configs="teamConfigs"
            :routing-types="routingTypes"
            :agent-bots="agentBots"
            @update-config="handleConfigUpdate"
          />
        </div>

        <div class="w-full">
          <NextButton
            type="submit"
            :is-loading="isCreating"
            solid
            teal
            :label="$t('INBOX_MGMT.TEAMS.BUTTON_TEXT')"
          />
        </div>
      </div>
    </form>
  </div>
</template>
